import { useState, useEffect, useRef } from 'react'
import { equals } from 'ramda'
import debounce from 'lodash/debounce'

import { queryBuilder } from '~/core/utils/functions'
import { api } from '~/core/api/api'

const useIpbxApi = ({ url, query, name, ...requestOptions }, defaultValue = null, opts = []) => {
  const [{ loading, error, data }, setResponse] = useState({
    loading: false,
    error: null,
    data: defaultValue
  })
  const [fetch, setFetch] = useState({
    url: url ? queryBuilder(url, query) : null,
    requestOptions
  })

  const handleRefetch = params => {
    const { url, query, ...newOptions } = params || {}

    if (!params) {
      // Do simple refetch based on last request configuration
      setFetch({
        ...fetch
      })
    }
    if (url) {
      setFetch({
        url: queryBuilder(url, query),
        requestOptions: newOptions
      })
    }
  }

  const handleReset = () => {
    setResponse({
      loading: false,
      error: null,
      data: defaultValue
    })
  }

  useEffect(() => {
    const { url, requestOptions } = fetch
    let canceled = false

    async function makeRequest() {
      setResponse(state => ({
        ...state,
        error: null,
        loading: !requestOptions.skipLoading
      }))

      try {
        const result = await api.request({
          url,
          method: 'get',
          ...requestOptions
        })

        if (!canceled) {
          const data =
            typeof requestOptions.transformData === 'function'
              ? await requestOptions.transformData(result.data)
              : result.data

          if (typeof requestOptions.onSuccess === 'function') {
            requestOptions.onSuccess(data)
          }

          setResponse(state => ({
            ...state,
            loading: false,
            error: null,
            ...(requestOptions.skipResult ? null : { data })
          }))
        }
      } catch (error) {
        const errorObject = {
          statusCode: error ? error.response?.status : 500,
          data: error ? error.response?.data || error.message : 'Internal server error'
        }
        console.error(`[${name || 'useIpbxApi'}] `, errorObject)
        setResponse(state => ({
          ...state,
          loading: false,
          error: errorObject
        }))
      }
    }

    if (url) {
      makeRequest()
    }

    return () => {
      canceled = true
    }
  }, [fetch, ...opts])

  return [{ loading, error, data }, handleRefetch, handleReset]
}

const useClick = (ref, action, dependable, opts = []) => {
  const callback = useRef(action)

  useEffect(() => {
    callback.current = action
  })

  useEffect(() => {
    const handleClick = event => {
      if (ref && ref.current && ref.current === event.target) {
        return callback.current(event)
      }
    }
    // If 'dependable' arg is not set, always register 'click' event
    if (dependable === undefined) {
      document.addEventListener('click', handleClick)
    } else if (dependable) {
      document.addEventListener('click', handleClick)
    }

    return () => document.removeEventListener('click', handleClick)
  }, [dependable, ...opts])
}

const useOutsideClick = (ref, action, dependable, opts = []) => {
  const handleOutsideClick = ({ target }) => {
    if (ref && ref.current && !ref.current.contains(target)) return action()
  }

  useEffect(() => {
    if (dependable) {
      document.addEventListener('mousedown', handleOutsideClick)
      document.addEventListener('touchstart', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [dependable, ...opts])
}

const useKeyPress = (keyCode, action, dependable, opts = []) => {
  const callback = useRef(action)

  useEffect(() => {
    callback.current = action
  })

  useEffect(() => {
    const handleKeydown = event => {
      if (event.code === keyCode) {
        return callback.current(event)
      }
    }
    // If 'dependable' arg is not set, always register 'keydown' event
    if (dependable === undefined) {
      document.addEventListener('keydown', handleKeydown)
    } else if (dependable) {
      document.addEventListener('keydown', handleKeydown)
    }

    return () => document.removeEventListener('keydown', handleKeydown)
  }, [dependable, ...opts])
}

const useObserver = (value, action, dependable) => {
  const previous = useRef(value)
  const actionRef = useRef(action)

  useEffect(() => {
    actionRef.current = action
  })

  useEffect(() => {
    if (dependable || dependable === undefined) {
      if (!equals(previous.current, value)) {
        actionRef.current(value, previous.current)
      }
      previous.current = value
    }
  }, [dependable, value])
}

const useResize = (ref, obtainDefaultSize = true) => {
  const [size, setSize] = useState({
    height: null,
    width: null
  })

  const handleResize = debounce(() => {
    const { width, height } = ref.current.getBoundingClientRect()
    setSize({ width, height })
  }, 50)

  useEffect(() => {
    if (ref && ref.current) {
      if (obtainDefaultSize) {
        const { width, height } = ref.current.getBoundingClientRect()
        setSize({ width, height })
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [])

  return size
}

const useBeforeUnload = (dependable, opts = []) => {
  const handleBeforeUnload = e => {
    if (dependable || dependable === undefined) {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [dependable, ...opts])
}

const useBoundingClientRect = (action, dependable, opts = []) => {
  const ref = useRef(null)

  useEffect(() => {
    if (dependable || dependable === undefined) {
      if (!ref.current) return
      const value = ref.current.getBoundingClientRect()
      action(value)
    }
  }, [dependable, ...opts])

  return ref
}

const useDexieTable = ({ table, listenToChanges = true, transformData }, defaultValue = []) => {
  const [state, setState] = useState({
    data: defaultValue,
    error: null,
    loading: false
  })

  useEffect(() => {
    let subscribeListeners = null

    const getData = async () => {
      setState(state => ({
        ...state,
        loading: true,
        error: null
      }))
      try {
        const data = await table.toArray()
        setState({
          data: typeof transformData === 'function' ? await transformData(data) : data,
          loading: false,
          error: null
        })
      } catch (error) {
        setState(state => ({
          ...state,
          loading: false,
          error
        }))
      }
    }

    const subscribeChanges = listener => {
      const events = ['creating', 'updating', 'deleting']
      events.forEach(ev => table.hook(ev, listener))
      return {
        unsubscribe: () => {
          events.forEach(ev => table.hook(ev).unsubscribe(listener))
          return null
        }
      }
    }

    const changesListener = (...args) => {
      // Number od arguments may change, the last one is always transaction
      const transaction = args[args.length - 1]
      transaction.on('complete', () => {
        if (transaction.storeNames.includes(table.name)) {
          getData()
        }
      })
    }

    if (listenToChanges) {
      subscribeListeners = subscribeChanges(changesListener)
    }

    getData()

    return () => {
      if (subscribeListeners !== null) {
        subscribeListeners = subscribeListeners.unsubscribe()
      }
    }
  }, [table])

  return [state]
}

const useEvent = (eventName, action, dependable) => {
  const callback = useRef(action)

  useEffect(() => {
    callback.current = action
  })

  useEffect(() => {
    const handleEvent = event => callback.current(event)
    if (!eventName) return

    if (dependable || dependable === undefined) {
      document.addEventListener(eventName, handleEvent)
    }

    return () => document.removeEventListener(eventName, handleEvent)
  }, [dependable])
}

//@flow
const useRowCheck = (defaultValue: string[]) => {
  const [checkedRows, setCheckedRows] = useState(defaultValue)

  const handleRowCheck = (id: string) => {
    if (checkedRows.includes(id)) {
      return setCheckedRows(checkedRows.filter(rowId => rowId !== id))
    }
    setCheckedRows([...checkedRows, id])
  }

  const handleRowCheckAll = (ids: string[]) => setCheckedRows(ids)

  return [
    {
      checkedRows,
      onRowCheck: handleRowCheck,
      onRowCheckAll: handleRowCheckAll
    },
    setCheckedRows
  ]
}

const useFileReader = ({
  onLoad,
  onError
}: {
  onLoad: (result: ArrayBuffer, event: ProgressEvent<FileReader>) => void,
  onError: (event: ProgressEvent<FileReader>) => void
}) => {
  const reader = useRef(new window.FileReader())

  const handleLoad = event => onLoad(reader.current.result, event)

  useEffect(() => {
    reader.current.addEventListener('load', handleLoad)
    reader.current.addEventListener('error', onError)
    return () => {
      reader.current.removeEventListener('load', handleLoad)
      reader.current.removeEventListener('load', onError)
    }
  }, [])

  return reader.current
}

const useInterval = (callback: () => void, delay: number | null | false, immediate?: boolean) => {
  const savedCallback = useRef(() => {})

  useEffect(() => {
    savedCallback.current = callback
  })

  useEffect(() => {
    if (!immediate || delay === null || delay === false) {
      return undefined
    }

    savedCallback.current()
  }, [immediate])

  useEffect(() => {
    if (delay === null || delay === false) {
      return undefined
    }

    const id = window.setInterval(savedCallback.current, delay)
    return () => window.clearInterval(id)
  }, [delay])
}

const useFullscreen = () => {
  const ref = useRef(null)
  const [isFullscreen, setFullscreen] = useState(false)
  const isFullscreenSupported = document.fullscreenEnabled

  const handleFullscreenChange = () => setFullscreen(document.fullscreenElement !== null)

  const requestFullscreen = () => {
    if (ref.current) {
      ref.current.requestFullscreen().catch(err => console.error('[useFullscreen] Cannot request fullscreen', err))
    }
  }

  const exitFullscreen = () =>
    document.exitFullscreen().catch(err => console.error('[useFullscreen] Cannot exit fullscreen', err))

  useEffect(() => {
    if (isFullscreenSupported) {
      document.addEventListener('fullscreenchange', handleFullscreenChange)
    }
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  return { ref, isFullscreen, isFullscreenSupported, requestFullscreen, exitFullscreen }
}

export {
  useIpbxApi,
  useClick,
  useKeyPress,
  useObserver,
  useResize,
  useOutsideClick,
  useBeforeUnload,
  useBoundingClientRect,
  useDexieTable,
  useEvent,
  useRowCheck,
  useFileReader,
  useInterval,
  useFullscreen
}
